import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/lib/toast";
import apiClient from "@/lib/api-client";
import { guideSignals } from "@/lib/guideSignals";
import type { ResultFingerprint } from "@/lib/resultFingerprint";

interface PrimaryAlignment {
  camp_id: string;
  camp_label: string;
  confidence: number;
  rationale: string;
}

interface TensionEngaged {
  camp_a: string;
  camp_b: string;
  stance: string;
  explanation: string;
}

interface GapAddressed {
  blind_spot: string;
  how_addressed: string;
}

export interface PositionAnalysis {
  result: {
    primary_alignment: PrimaryAlignment;
    tensions_engaged: TensionEngaged[];
    gaps_addressed: GapAddressed[];
    positioning_statement: string;
    novelty_assessment: string;
  };
  generated_at: string;
  model: string;
  /** Snapshot of the corpus scope the analysis was grounded in.
   * Absent on results generated before the fingerprint primitive landed. */
  fingerprint?: ResultFingerprint;
}

export function usePositionAnalysis(conceptId: string) {
  const queryClient = useQueryClient();

  const analyzeMutation = useMutation({
    mutationKey: ["position-analyze", conceptId],
    mutationFn: () =>
      apiClient
        .post(`api/concepts/${conceptId}/position-analysis`, {
          timeout: 120_000,
        })
        .json<PositionAnalysis>(),
    onSuccess: () => {
      toast.success("Position analysis complete");
      // Track usage for guide milestones
      guideSignals.mark(conceptId, 'hasUsedPositionAnalysis');
      // Invalidate concept query to pick up the new position_analysis.
      // PLURAL ["concepts", id] — useConcept's key, which feeds
      // useAnalyzeToolState. The old singular key matched only the retired
      // build.tsx ad-hoc query, so results never refreshed elsewhere.
      queryClient.invalidateQueries({ queryKey: ["concepts", conceptId] });
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : "Analysis failed";
      toast.error("Position analysis failed", { description: msg });
    },
  });

  const feedbackMutation = useMutation({
    mutationFn: (body: { signal: string; comment?: string | null }) =>
      apiClient
        .post(`api/concepts/${conceptId}/position-analysis/feedback`, {
          json: body,
        })
        .json(),
    onSuccess: () => toast.success("Feedback submitted"),
  });

  return {
    analyze: analyzeMutation,
    submitFeedback: feedbackMutation,
  };
}
